/**
 * ShieldEnemy - Armored enemy with a protective shield
 * 
 * Has a front-facing shield that blocks player attacks from the side.
 * Can only be defeated by landing on top.
 * Periodically pauses to slam its shield, creating a shockwave visual.
 * Features animated shield, glowing core, and stomping behavior.
 * 
 * @module objects/ShieldEnemy
 */

import Phaser from 'phaser';

/** Shield enemy states */
const ShieldState = {
    PATROL: 'patrol',
    SLAM: 'slam',
    RECOVERY: 'recovery'
};

export default class ShieldEnemy {
    /**
     * @param {Phaser.Scene} scene - The game scene
     * @param {number} x - Spawn X position
     * @param {number} y - Spawn Y position
     * @param {Object} [options] - Configuration options
     * @param {number} [options.speed=1.2] - Patrol speed
     * @param {number} [options.walkDist=100] - Patrol range
     * @param {number} [options.slamInterval=3000] - ms between slams
     * @param {number} [options.shieldColor=0x44aaff] - Shield color
     * @param {number} [options.bodyColor=0x445566] - Body color
     */
    constructor(scene, x, y, options = {}) {
        this.scene = scene;
        this.startPos = { x, y };
        this.isDead = false;
        this.type = 'shield';

        // Configuration
        this.speed = options.speed ?? 1.2;
        this.walkDist = options.walkDist ?? 100;
        this.slamInterval = options.slamInterval ?? 3000;
        this.shieldColor = options.shieldColor ?? 0x44aaff;
        this.bodyColor = options.bodyColor ?? 0x445566;

        // State
        this.state = ShieldState.PATROL;
        this.moveDir = 1;
        this.stateTimer = 0;
        this.lastSlamTime = 0;
        this.bounceTimer = 0;
        this.shieldPulse = 0;

        // Create visual components
        this.createVisuals(x, y);

        // Create physics body
        this.createPhysicsBody(x, y);
    }

    /**
     * Create all visual elements with vector graphics
     */
    createVisuals(x, y) {
        const size = 44;

        // Shadow
        this.shadow = this.scene.add.ellipse(x, y + 28, 40, 10, 0x000000, 0.3);

        // Main body (rounded square)
        this.body = this.scene.add.graphics();
        this.drawBody(x, y);

        // Physics attachment sprite
        this.sprite = this.scene.add.rectangle(x, y, size, size, 0x000000, 0);

        // Shield (arc in front)
        this.shield = this.scene.add.graphics();
        this.drawShield(x, y);

        // Core glow
        this.core = this.scene.add.circle(x, y, 6, 0x00ffcc, 0.8);
        this.coreGlow = this.scene.add.circle(x, y, 12, 0x00ffcc, 0.15);

        // Eyes (narrow, menacing)
        this.leftEye = this.scene.add.rectangle(x - 9, y - 8, 8, 4, 0xff3333);
        this.rightEye = this.scene.add.rectangle(x + 9, y - 8, 8, 4, 0xff3333);

        // Helmet visor line
        this.visor = this.scene.add.rectangle(x, y - 14, 30, 2, 0x88ccff, 0.5);

        // Animate core
        this.scene.tweens.add({
            targets: [this.coreGlow],
            alpha: 0.35,
            scale: 1.4,
            duration: 1200,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
    }

    /**
     * Draw the body shape
     * @param {number} x
     * @param {number} y
     */
    drawBody(x, y) {
        this.body.clear();

        // Armor plating
        this.body.fillStyle(this.bodyColor, 1);
        this.body.fillRoundedRect(x - 22, y - 20, 44, 40, 6);

        // Top highlight
        this.body.fillStyle(0xffffff, 0.15);
        this.body.fillRoundedRect(x - 20, y - 18, 40, 12, 4);

        // Bottom edge shadow
        this.body.fillStyle(0x000000, 0.25);
        this.body.fillRoundedRect(x - 20, y + 10, 40, 8, 3);

        // Armor lines
        this.body.lineStyle(1, 0xffffff, 0.1);
        this.body.lineBetween(x - 18, y - 4, x + 18, y - 4);
        this.body.lineBetween(x - 18, y + 4, x + 18, y + 4);
    }

    /**
     * Draw the shield
     * @param {number} x
     * @param {number} y
     */
    drawShield(x, y) {
        this.shield.clear();

        const shieldDir = this.moveDir;
        const sx = x + shieldDir * 22;

        // Shield arc
        this.shield.fillStyle(this.shieldColor, 0.5 + this.shieldPulse * 0.3);
        this.shield.beginPath();
        this.shield.arc(sx, y, 18, 
            shieldDir > 0 ? -Math.PI * 0.4 : Math.PI * 0.6, 
            shieldDir > 0 ? Math.PI * 0.4 : Math.PI * 1.4, 
            false
        );
        this.shield.closePath();
        this.shield.fill();

        // Shield edge glow
        this.shield.lineStyle(2, this.shieldColor, 0.7 + this.shieldPulse * 0.3);
        this.shield.beginPath();
        this.shield.arc(sx, y, 18,
            shieldDir > 0 ? -Math.PI * 0.4 : Math.PI * 0.6,
            shieldDir > 0 ? Math.PI * 0.4 : Math.PI * 1.4,
            false
        );
        this.shield.stroke();

        // Inner energy lines
        this.shield.lineStyle(1, 0xffffff, 0.2);
        this.shield.beginPath();
        this.shield.arc(sx, y, 12,
            shieldDir > 0 ? -Math.PI * 0.3 : Math.PI * 0.7,
            shieldDir > 0 ? Math.PI * 0.3 : Math.PI * 1.3,
            false
        );
        this.shield.stroke();
    }

    /**
     * Create physics body
     */
    createPhysicsBody(x, y) {
        const { Bodies } = Phaser.Physics.Matter.Matter;
        const enemyBody = Bodies.rectangle(x, y, 44, 40, {
            isStatic: false,
            friction: 0.1,
            frictionAir: 0.02,
            density: 0.004,
            label: 'enemy'
        });

        this.scene.matter.add.gameObject(this.sprite, enemyBody);
        this.sprite.setFixedRotation();
        this.sprite.enemyObject = this;
    }

    /**
     * Update loop with state machine behavior
     */
    update() {
        if (this.isDead || !this.sprite.active) return;

        const now = this.scene.time.now;

        switch (this.state) {
            case ShieldState.PATROL:
                this.updatePatrol(now);
                break;
            case ShieldState.SLAM:
                this.updateSlam(now);
                break;
            case ShieldState.RECOVERY:
                this.updateRecovery(now);
                break;
        }

        this.updateVisuals();
    }

    /**
     * Patrol state - walk back and forth
     */
    updatePatrol(now) {
        // Direction change at bounds
        if (this.sprite.x > this.startPos.x + this.walkDist) {
            this.moveDir = -1;
        } else if (this.sprite.x < this.startPos.x - this.walkDist) {
            this.moveDir = 1;
        }

        this.sprite.setVelocityX(this.moveDir * this.speed);

        // Check if time for slam
        if (now - this.lastSlamTime > this.slamInterval) {
            this.state = ShieldState.SLAM;
            this.stateTimer = now;
            this.sprite.setVelocityX(0);
        }

        this.bounceTimer += 0.12;
    }

    /**
     * Slam state - shield bash effect
     */
    updateSlam(now) {
        const elapsed = now - this.stateTimer;
        this.shieldPulse = Math.min(1, elapsed / 300);

        if (elapsed > 400) {
            this.createShockwave();
            this.state = ShieldState.RECOVERY;
            this.stateTimer = now;
            this.lastSlamTime = now;
        }

        this.sprite.setVelocityX(0);
    }

    /**
     * Recovery state - brief pause after slam
     */
    updateRecovery(now) {
        const elapsed = now - this.stateTimer;
        this.shieldPulse = Math.max(0, 1 - elapsed / 400);

        if (elapsed > 600) {
            this.state = ShieldState.PATROL;
            this.shieldPulse = 0;
        }

        this.sprite.setVelocityX(0);
    }

    /**
     * Create visual shockwave effect
     */
    createShockwave() {
        const x = this.sprite.x + this.moveDir * 24;
        const y = this.sprite.y;

        // Shockwave ring
        const ring = this.scene.add.circle(x, y, 5, this.shieldColor, 0.6);
        this.scene.tweens.add({
            targets: ring,
            scale: 4,
            alpha: 0,
            duration: 500,
            ease: 'Power2',
            onComplete: () => ring.destroy()
        });

        // Energy particles
        for (let i = 0; i < 6; i++) {
            const angle = (this.moveDir > 0 ? -0.5 : 0.5) * Math.PI + (i / 6) * Math.PI;
            const particle = this.scene.add.circle(x, y, 3, this.shieldColor, 0.7);
            this.scene.tweens.add({
                targets: particle,
                x: x + Math.cos(angle) * 40,
                y: y + Math.sin(angle) * 40,
                alpha: 0,
                duration: 400,
                ease: 'Power2',
                onComplete: () => particle.destroy()
            });
        }

        // Camera micro-shake
        this.scene.cameras.main.shake(60, 0.002);
    }

    /**
     * Sync all visual elements with physics body position
     */
    updateVisuals() {
        const x = this.sprite.x;
        const y = this.sprite.y;
        const bounce = Math.sin(this.bounceTimer) * 2;

        // Body
        this.drawBody(x, y + bounce * 0.3);

        // Shield
        this.drawShield(x, y + bounce * 0.3);

        // Core
        this.core.setPosition(x, y + bounce * 0.3);
        this.coreGlow.setPosition(x, y + bounce * 0.3);

        // Eyes (narrow slits)
        this.leftEye.setPosition(x - 9, y - 8 + bounce * 0.3);
        this.rightEye.setPosition(x + 9, y - 8 + bounce * 0.3);

        // Visor
        this.visor.setPosition(x, y - 14 + bounce * 0.3);

        // Shadow
        this.shadow.setPosition(x, this.startPos.y + 28);
    }

    /**
     * Kill with explosion effect
     */
    kill() {
        if (this.isDead) return;
        this.isDead = true;
        this.createDeathEffect();
        this.destroyVisuals();
    }

    /**
     * Shield shatter death effect
     */
    createDeathEffect() {
        const x = this.sprite.x;
        const y = this.sprite.y;

        // Shield shard particles
        for (let i = 0; i < 12; i++) {
            const angle = (i / 12) * Math.PI * 2;
            const speed = Phaser.Math.Between(40, 80);
            const color = i % 2 === 0 ? this.shieldColor : this.bodyColor;
            const shard = this.scene.add.rectangle(
                x, y,
                Phaser.Math.Between(4, 10),
                Phaser.Math.Between(4, 10),
                color
            );

            this.scene.tweens.add({
                targets: shard,
                x: x + Math.cos(angle) * speed,
                y: y + Math.sin(angle) * speed + 30,
                alpha: 0,
                rotation: Phaser.Math.Between(-6, 6),
                duration: 700,
                ease: 'Power2',
                onComplete: () => shard.destroy()
            });
        }

        // Flash
        const flash = this.scene.add.circle(x, y, 30, this.shieldColor, 0.6);
        this.scene.tweens.add({
            targets: flash,
            scale: 3,
            alpha: 0,
            duration: 400,
            onComplete: () => flash.destroy()
        });
    }

    /**
     * Safely destroy all visuals
     */
    destroyVisuals() {
        const visuals = [
            this.sprite, this.body, this.shield,
            this.core, this.coreGlow,
            this.leftEye, this.rightEye,
            this.visor, this.shadow
        ];

        visuals.forEach(v => {
            if (v && v.destroy && !v.destroyed) {
                v.destroy();
            }
        });

        this.sprite = null;
        this.body = null;
        this.shield = null;
    }

    /**
     * Full cleanup
     */
    destroy() {
        this.isDead = true;
        this.destroyVisuals();
    }
}
